import { useState, useEffect } from 'react';
import { Save, Users, Calendar, BookOpen, UserCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api, { classApi, studentApi, teacherApi } from '../lib/api';

// Liste des mois en français et arabe
const MONTHS = [
  { value: 1, label_fr: 'Janvier', label_ar: 'يناير' },
  { value: 2, label_fr: 'Février', label_ar: 'فبراير' },
  { value: 3, label_fr: 'Mars', label_ar: 'مارس' },
  { value: 4, label_fr: 'Avril', label_ar: 'أبريل' },
  { value: 5, label_fr: 'Mai', label_ar: 'مايو' },
  { value: 6, label_fr: 'Juin', label_ar: 'يونيو' },
  { value: 7, label_fr: 'Juillet', label_ar: 'يوليو' },
  { value: 8, label_fr: 'Août', label_ar: 'أغسطس' },
  { value: 9, label_fr: 'Septembre', label_ar: 'سبتمبر' },
  { value: 10, label_fr: 'Octobre', label_ar: 'أكتوبر' },
  { value: 11, label_fr: 'Novembre', label_ar: 'نوفمبر' },
  { value: 12, label_fr: 'Décembre', label_ar: 'ديسمبر' },
];

// Statuts d'évaluation - Types d'évaluation coranique
const EVAL_TYPES = [
  { value: 'bind', label: 'بند', label_fr: 'Bind' },
  { value: 'wett', label: 'وط', label_fr: 'Wett' },
  { value: 'nafarr', label: 'نفر', label_fr: 'Nafarr' },
];

// Options pour le menu déroulant Raisons (اسباب)
const REASON_OPTIONS = [
  { value: 'haadir', label: 'حـ' },
  { value: 'lam', label: 'لم' },
  { value: 'ghayb', label: 'غ' },
  { value: 'marid', label: 'م' },
];

export default function MonthlyEvalPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEvaluator, setSelectedEvaluator] = useState('');
  const [evaluations, setEvaluations] = useState({});
  const [existingEvals, setExistingEvals] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass && selectedMonth && selectedYear) {
      fetchExistingEvals();
    }
  }, [selectedClass, selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, studentsRes, teachersRes] = await Promise.all([
        classApi.getAll({}),
        studentApi.getAll({ is_active: true }),
        teacherApi.getAll({ is_active: true }),
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingEvals = async () => {
    try {
      const response = await api.get(`/monthly-evals?class_id=${selectedClass}&month=${selectedMonth}&year=${selectedYear}`);
      setExistingEvals(response.data);
      
      // Pré-remplir les évaluations existantes
      const evalsMap = {};
      response.data.forEach(item => {
        evalsMap[item.student_id] = item;
        // Pré-sélectionner l'examinateur s'il existe
        if (item.evaluator_id && !selectedEvaluator) {
          setSelectedEvaluator(item.evaluator_id);
        }
      });
      setEvaluations(evalsMap);
    } catch (error) {
      console.error('Erreur chargement évaluations:', error);
    }
  };

  const getClassStudents = () => {
    return students.filter(s => s.class_id === selectedClass);
  };

  const getClassName = () => {
    const cls = classes.find(c => c.id === selectedClass);
    return cls?.name || '';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : '';
  };

  const getClassTeacher = () => {
    const cls = classes.find(c => c.id === selectedClass);
    if (cls?.teacher_id) {
      return getTeacherName(cls.teacher_id);
    }
    return '';
  };

  const updateEvaluation = (studentId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        student_id: studentId,
        class_id: selectedClass,
        evaluator_id: selectedEvaluator,
        month: selectedMonth,
        year: selectedYear,
        [field]: value,
      }
    }));
  };

  const getEvaluation = (studentId) => {
    return evaluations[studentId] || {};
  };

  const handleSaveAll = async () => {
    if (!selectedEvaluator) {
      toast.error('Veuillez sélectionner un examinateur');
      return;
    }

    const evalsToSave = Object.values(evaluations).filter(e => 
      e.current_level || e.last_lesson || e.count_bind > 0 || e.count_wett > 0 || e.count_nafarr > 0
    );
    
    if (evalsToSave.length === 0) {
      toast.error('Aucune évaluation à enregistrer');
      return;
    }

    // S'assurer que l'evaluator_id est correct pour toutes les évaluations
    const evaluationsWithEvaluator = evalsToSave.map(evalItem => ({
      ...evalItem,
      evaluator_id: selectedEvaluator,
      month: selectedMonth,
      year: selectedYear,
      class_id: selectedClass,
    }));

    setSaving(true);
    try {
      await api.post('/monthly-evals/batch', { evaluations: evaluationsWithEvaluator });
      toast.success(`${evaluationsWithEvaluator.length} évaluations enregistrées`);
      fetchExistingEvals();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const classStudents = getClassStudents();

  // Générer les années disponibles (année actuelle ± 2)
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="monthly-eval-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">مراقبات شهرية</h1>
          <p className="text-muted-foreground text-sm">Évaluations Mensuelles - Par un examinateur externe</p>
        </div>
        <Button 
          className="rounded-full" 
          onClick={handleSaveAll}
          disabled={saving || !selectedClass || !selectedEvaluator}
          data-testid="save-all-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer tout'}
        </Button>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>الفصل (Classe)</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger data-testid="select-class">
                  <SelectValue placeholder="Sélectionner une classe" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>الشهر (Mois)</Label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger data-testid="select-month">
                  <SelectValue placeholder="Mois" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>
                      {m.label_fr} ({m.label_ar})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>السنة (Année)</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="select-year">
                  <SelectValue placeholder="Année" />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>اسم المعلم (Examinateur)</Label>
              <Select value={selectedEvaluator} onValueChange={setSelectedEvaluator}>
                <SelectTrigger data-testid="select-evaluator">
                  <SelectValue placeholder="Sélectionner l'examinateur" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.first_name} {t.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info classe */}
      {selectedClass && (
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <Badge variant="outline" className="gap-1">
            <Users className="w-3 h-3" />
            {classStudents.length} élèves
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Calendar className="w-3 h-3" />
            {MONTHS.find(m => m.value === selectedMonth)?.label_fr} {selectedYear}
          </Badge>
          {getClassTeacher() && (
            <Badge variant="outline" className="gap-1">
              <UserCheck className="w-3 h-3" />
              Prof: {getClassTeacher()}
            </Badge>
          )}
          {selectedEvaluator && (
            <Badge variant="secondary" className="gap-1">
              <BookOpen className="w-3 h-3" />
              Examinateur: {getTeacherName(selectedEvaluator)}
            </Badge>
          )}
        </div>
      )}

      {/* Tableau d'évaluation */}
      {selectedClass ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5" />
              {getClassName()} - مراقبات شهرية
            </CardTitle>
            <CardDescription>
              الرقم (N°) | الاسم (Nom) | المستوى (Niveau) | بند (Bind) | وط (Wett) | نفر (Nafarr) | اسباب (Raisons) | الدرس الاخير (Dernière leçon)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[50px] text-center">الرقم<br/><span className="text-xs text-muted-foreground">N°</span></TableHead>
                    <TableHead className="min-w-[150px]">الاسم<br/><span className="text-xs text-muted-foreground">Nom</span></TableHead>
                    <TableHead className="min-w-[120px]">المستوى<br/><span className="text-xs text-muted-foreground">Niveau</span></TableHead>
                    <TableHead className="text-center w-[70px] bg-green-50">بند<br/><span className="text-xs text-muted-foreground">Bind</span></TableHead>
                    <TableHead className="text-center w-[70px] bg-yellow-50">وط<br/><span className="text-xs text-muted-foreground">Wett</span></TableHead>
                    <TableHead className="text-center w-[70px] bg-blue-50">نفر<br/><span className="text-xs text-muted-foreground">Nafarr</span></TableHead>
                    <TableHead className="min-w-[120px]">اسباب<br/><span className="text-xs text-muted-foreground">Raisons</span></TableHead>
                    <TableHead className="min-w-[140px]">الدرس الاخير<br/><span className="text-xs text-muted-foreground">Dernière leçon</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {classStudents.map((student, idx) => {
                    const evalData = getEvaluation(student.id);
                    return (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium text-center">{idx + 1}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{student.first_name} {student.last_name}</p>
                            <p className="text-xs text-muted-foreground">{student.matricule}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={evalData.current_level || ''}
                            onChange={(e) => updateEvaluation(student.id, 'current_level', e.target.value)}
                            placeholder="Ex: واذالقنا"
                            className="h-9 text-right"
                            dir="rtl"
                          />
                        </TableCell>
                        <TableCell className="bg-green-50/50">
                          <Input
                            type="number"
                            min="0"
                            value={evalData.count_bind || ''}
                            onChange={(e) => updateEvaluation(student.id, 'count_bind', parseInt(e.target.value) || 0)}
                            className="h-9 w-16 text-center"
                          />
                        </TableCell>
                        <TableCell className="bg-yellow-50/50">
                          <Input
                            type="number"
                            min="0"
                            value={evalData.count_wett || ''}
                            onChange={(e) => updateEvaluation(student.id, 'count_wett', parseInt(e.target.value) || 0)}
                            className="h-9 w-16 text-center"
                          />
                        </TableCell>
                        <TableCell className="bg-blue-50/50">
                          <Input
                            type="number"
                            min="0"
                            value={evalData.count_nafarr || ''}
                            onChange={(e) => updateEvaluation(student.id, 'count_nafarr', parseInt(e.target.value) || 0)}
                            className="h-9 w-16 text-center"
                          />
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={evalData.absence_reason || ''} 
                            onValueChange={(v) => updateEvaluation(student.id, 'absence_reason', v)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="--" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">--</SelectItem>
                              {REASON_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={evalData.last_lesson || ''}
                            onChange={(e) => updateEvaluation(student.id, 'last_lesson', e.target.value)}
                            placeholder="Dernière leçon"
                            className="h-9 text-right"
                            dir="rtl"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">Sélectionnez une classe</h3>
            <p className="text-muted-foreground">Choisissez une classe pour commencer la saisie des évaluations mensuelles</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
